import React, { useState, useEffect, useCallback } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useDemoPermissions } from '../hooks/useDemoPermissions';
import { getHouseholdById, editHousehold } from '../services/householdService';
import { searchAddressSuggestions } from '../services/addressService';
import { searchClients } from '../services/clientService';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { terminology } from '../config/terminology';
import { FaPlus, FaTrashAlt } from 'react-icons/fa';
import { InputField, TextareaField } from '../components/fields';
import { debounce } from 'lodash';

const EditHouseholdPage = () => {
  const { id: householdId } = useParams(); // Get household ID from URL
  const navigate = useNavigate();
  const { isDemo } = useDemoPermissions();
  useEffect(() => { if (isDemo) navigate('/households'); }, [isDemo, navigate]);
  const [apiError, setApiError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [supplierName, setSupplierName] = useState(''); // To display the associated Supplier name
  const [originalFormData, setOriginalFormData] = useState({}); // Store original data for comparison
  const [successMessage, setSuccessMessage] = useState(null); // Success message state

  // Address search states
  const [addressSearchTerm, setAddressSearchTerm] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [lastSelectedAddress, setLastSelectedAddress] = useState(''); // Track last selected address

  // Supplier search states
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
  const [supplierSuggestions, setSupplierSuggestions] = useState([]);
  const [isSearchingSuppliers, setIsSearchingSuppliers] = useState(false);
  const [showSupplierSuggestions, setShowSupplierSuggestions] = useState(false);
  const [lastSelectedSupplier, setLastSelectedSupplier] = useState(''); // Track last selected supplier

  const { register, handleSubmit, reset, watch, control, setValue, formState: { errors, isSubmitting, isDirty } } = useForm({
    mode: 'onBlur',
    defaultValues: {
        clientId: '',
        clientNameSearch: '',
        firstName: '',
        lastName: '',
        addressLine1: '',
        addressLine2: '',
        state: '',
        postalCode: '',
        email: '',
        phoneNumber: '',
        country: 'Australia', // Default and read-only
        // New fields
        adminNotes: '',
        originalName: '',
        siteContact: {
          name: '',
          phone: '',
          email: ''
        },
        additionalContacts: [],
        status: '' // New field for status
    }
  });

  const emailValue = watch('email');
  const phoneValue = watch('phoneNumber');
  const supplierNameSearchValue = watch('clientNameSearch');
  const addressLine1Value = watch('addressLine1');

  // Set up field array for additional contacts
  const { fields: additionalContactFields, append: appendAdditionalContact, remove: removeAdditionalContact } = useFieldArray({
    control,
    name: 'additionalContacts'
  });

  useEffect(() => {
    const fetchHousehold = async () => {
      setLoading(true);
      setApiError(null);
      try {
        const data = await getHouseholdById(householdId);
        setSupplierName(data.clientName || 'N/A'); // Store supplier name for display
        // Map fetched data to form fields
        const formData = {
          clientId: data.clientId || '',
          clientNameSearch: data.clientName || '',
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          addressLine1: data.addressLine1 || '',
          addressLine2: data.addressLine2 || '',
          state: data.state || '',
          postalCode: data.postalCode || '',
          email: data.email || '',
          phoneNumber: data.phoneNumber || '',
          country: data.country || 'Australia',
          // Handle new structured contact fields
          adminNotes: data.adminNotes || '',
          originalName: data.originalName || '',
          siteContact: data.siteContact || {
            name: '',
            phone: '',
            email: ''
          },
          additionalContacts: data.additionalContacts || [],
          status: data.status || '' // Populate status from API response
        };
        reset(formData); // Populate form with fetched data
        setOriginalFormData(formData); // Store original data for comparison

        // Initialize search terms and set lastSelected values to prevent auto-search
        const initialAddress = formData.addressLine1 || '';
        const initialSupplier = formData.clientNameSearch || '';
        setSupplierSearchTerm(initialSupplier);
        setLastSelectedSupplier(initialSupplier); // Set this to match supplierSearchTerm to prevent search trigger
        setAddressSearchTerm(initialAddress);
        setLastSelectedAddress(initialAddress); // Set this to match addressSearchTerm to prevent search trigger
      } catch (error) {
        setApiError(error.message || `Failed to load ${terminology.household.singular.toLowerCase()} details.`);
        console.error("Fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchHousehold();
  }, [householdId, reset]);

  // Address search functionality
  const fetchAddressSuggestions = async (query) => {
    console.log('fetchAddressSuggestions called with:', query);
    if (!query || query.length < 3) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }
    
    console.log('Starting address search...');
    try {
      const suggestions = await searchAddressSuggestions(query);
      console.log('Address search results:', suggestions);
      setAddressSuggestions(suggestions.slice(0, 8)); // Limit to 8 results
      setShowAddressSuggestions(true);
      console.log('Showing address suggestions');
    } catch (error) {
      console.error('Error fetching address suggestions:', error);
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
    }
  };

  const debouncedFetchAddresses = useCallback(
    debounce(fetchAddressSuggestions, 300),
    []
  );

  // Address search effect
  useEffect(() => {
    console.log('Address search effect triggered:', {
      addressSearchTerm,
      addressLine1Value,
      lastSelectedAddress,
      shouldTrigger: addressSearchTerm.length >= 3 && 
        addressSearchTerm !== lastSelectedAddress
    });
    
    // Only trigger search if:
    // 1. Search term is 3+ characters
    // 2. Search term is different from the last selected address (user is typing something new)
    if (addressSearchTerm.length >= 3 && 
        addressSearchTerm !== lastSelectedAddress) {
      console.log('Triggering debounced address search');
      debouncedFetchAddresses(addressSearchTerm);
    } else {
      console.log('Skipping address search due to conditions');
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
    }
    return () => debouncedFetchAddresses.cancel();
  }, [addressSearchTerm, debouncedFetchAddresses, lastSelectedAddress]);

  // Address handlers
  const handleAddressSearchChange = (e) => {
    const value = e.target.value;
    console.log('handleAddressSearchChange called with:', value);
    
    setAddressSearchTerm(value);
    setValue('addressLine1', value, { shouldDirty: true, shouldValidate: true });
    
    if (value.length < 3) {
      console.log('Value too short, hiding suggestions');
      setShowAddressSuggestions(false);
      return;
    }
    
    // Only clear last selected address if user is typing something completely different
    // (not just a minor variation or continuation of the selected address)
    if (lastSelectedAddress && !value.startsWith(lastSelectedAddress.substring(0, Math.min(5, lastSelectedAddress.length)))) {
      console.log('User typing something new, clearing last selected address');
      setLastSelectedAddress('');
    }
  };

  const handleSelectAddress = (address) => {
    console.log('=== Address selection started ===');
    
    const { householdFormat } = address;
    console.log('Selected address:', address);
    console.log('HouseholdFormat:', householdFormat);
    
    const selectedAddressLine1 = householdFormat.addressLine1 || '';
    console.log('Setting form values...');
    
    setValue('addressLine1', selectedAddressLine1, { shouldDirty: true, shouldValidate: true });
    setValue('addressLine2', householdFormat.addressLine2 || '', { shouldDirty: true, shouldValidate: true });
    setValue('state', householdFormat.state || address.state || '', { shouldDirty: true, shouldValidate: true });
    setValue('postalCode', householdFormat.postalCode || address.postcode || '', { shouldDirty: true, shouldValidate: true });
    
    console.log('Updating search term and hiding dropdown...');
    setAddressSearchTerm(selectedAddressLine1);
    setLastSelectedAddress(selectedAddressLine1); // Remember this selection
    setAddressSuggestions([]);
    setShowAddressSuggestions(false);
    
    console.log('=== Address selection completed ===');
  };

  // Supplier search functionality
  const fetchSupplierSuggestions = async (searchTerm) => {
    if (searchTerm.length < 3) {
      setSupplierSuggestions([]);
      setShowSupplierSuggestions(false);
      return;
    }
    setIsSearchingSuppliers(true);
    try {
      const data = await searchClients({ searchString: searchTerm, pageSize: 5 });
      setSupplierSuggestions(data.clients || []);
      setShowSupplierSuggestions(true);
    } catch (error) {
      console.error(`${terminology.client.singular} search error:`, error);
      setSupplierSuggestions([]);
      setShowSupplierSuggestions(false);
    } finally {
      setIsSearchingSuppliers(false);
    }
  };

  const debouncedFetchSuppliers = useCallback(debounce(fetchSupplierSuggestions, 300), []);

  useEffect(() => {
    // Only trigger search if:
    // 1. Search term is 3+ characters
    // 2. Search term is different from the last selected supplier (user is typing something new)
    if (supplierSearchTerm.length >= 3 && supplierSearchTerm !== lastSelectedSupplier) {
      debouncedFetchSuppliers(supplierSearchTerm);
    } else {
      setSupplierSuggestions([]);
      setShowSupplierSuggestions(false);
    }
    return () => debouncedFetchSuppliers.cancel();
  }, [supplierSearchTerm, debouncedFetchSuppliers, lastSelectedSupplier]);

  const handleSupplierSearchChange = (e) => {
    const value = e.target.value;
    setSupplierSearchTerm(value);
    setValue('clientNameSearch', value);
    setValue('clientId', '');

    // Clear last selected supplier if user is typing something completely different
    if (lastSelectedSupplier && !value.startsWith(lastSelectedSupplier.substring(0, Math.min(5, lastSelectedSupplier.length)))) {
      setLastSelectedSupplier('');
    }

    if (value.length < 3) {
        setShowSupplierSuggestions(false);
    }
  };

  const handleSelectSupplier = (supplier) => {
    setValue('clientId', supplier.id, { shouldDirty: true, shouldValidate: true });
    setValue('clientNameSearch', supplier.clientName, { shouldDirty: true, shouldValidate: true });
    setSupplierSearchTerm(supplier.clientName);
    setLastSelectedSupplier(supplier.clientName); // Remember this selection

    const contactName = `${supplier.caseManagerFirstName || ''} ${supplier.caseManagerLastName || ''}`.trim();
    setValue('siteContact.name', contactName, { shouldDirty: true, shouldValidate: true });
    setValue('siteContact.email', supplier.caseManagerEmail || '', { shouldDirty: true, shouldValidate: true });
    setValue('siteContact.phone', supplier.phoneNumber || '', { shouldDirty: true, shouldValidate: true });

    setSupplierSuggestions([]);
    setShowSupplierSuggestions(false);
  };

  const onSubmit = async (data) => {
    setApiError(null);
    setSuccessMessage(null);

    // Validate clientId is selected
    if (!data.clientId) {
        setApiError(`Please select a valid ${terminology.client.singular.toLowerCase()}.`);
        return;
    }

    // Store clientNameSearch before removing it from submission data
    const clientNameSearch = data.clientNameSearch;

    // Remove clientNameSearch from submission data
    const submitData = { ...data };
    delete submitData.clientNameSearch;

    // Only include fields that have changed from original data
    const changedFields = {};
    
    // Fields we want to check for changes
    const fieldKeys = [
      'firstName', 'lastName', 'addressLine1', 'addressLine2', 
      'state', 'postalCode', 'email', 'phoneNumber', 'country',
      'adminNotes', 'originalName', 'siteContact', 'additionalContacts', 'status'
    ];
    
    fieldKeys.forEach(key => {
      // Check if the field has changed from its original value
      const originalValue = originalFormData[key];
      const currentValue = submitData[key];
      
      // Special handling for nested objects and arrays
      if (key === 'siteContact' || key === 'additionalContacts') {
        if (JSON.stringify(originalValue) !== JSON.stringify(currentValue)) {
          changedFields[key] = currentValue;
        }
      } else {
        // Include field if it's changed, making sure empty values are sent as empty strings
        if (originalValue !== currentValue) {
          changedFields[key] = currentValue === undefined || currentValue === null ? '' : currentValue;
        }
      }
    });

    // Check if supplier (clientId) has changed
    const supplierChanged = submitData.clientId && submitData.clientId !== originalFormData.clientId;
    const oldSupplierName = supplierName; // Store the old supplier name for message

    if (supplierChanged) {
      changedFields.clientId = submitData.clientId;
      // Get client name from clientNameSearch
      changedFields.clientName = clientNameSearch || supplierName;
    }
    // If no fields have changed, don't submit
    if (Object.keys(changedFields).length === 0 && !data.clientId) {
      setApiError('No changes detected to save.');
      return;
    }
    
    // clientId might be needed for the backend to identify the record relationship
    if (data.clientId) {
      changedFields.clientId = data.clientId;
    }

    try {
      const response = await editHousehold(householdId, changedFields);

      // Build success message
      let message = `${terminology.household.singular} updated successfully!`;

      // Check if supplier changed and if there's information about job transfers
      if (supplierChanged && response && response.message) {
        // Check if the response message contains information about job schedule transfers
        if (response.message.includes('new job schedule') || response.message.includes('pending jobs')) {
          message += `\n\nPending jobs for this ${terminology.household.singular.toLowerCase()} under supplier "${oldSupplierName}" have been transferred and assigned to "${clientNameSearch}".`;
        }
      }

      setSuccessMessage(message);

      // Auto-dismiss success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);

      // Reset the form with the latest values to update dirty state
      const newFormData = { ...originalFormData, ...changedFields };

      // Update supplier name if it changed
      if (supplierChanged) {
        setSupplierName(clientNameSearch);
        setSupplierSearchTerm(clientNameSearch);
        // Update the form's clientNameSearch and clientId fields as well
        newFormData.clientNameSearch = clientNameSearch;
        newFormData.clientId = submitData.clientId;
      }

      reset(newFormData);
      setOriginalFormData(newFormData);

      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setApiError(error.message || `Failed to update ${terminology.household.singular.toLowerCase()}. Please try again.`);
      console.error("Update error:", error);
      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (loading) {
    return <p className="text-center text-gray-500 py-10">Loading {terminology.household.singular.toLowerCase()} details...</p>;
  }

  return (
    <div className="container mx-auto px-4 pt-8 pb-20 max-w-3xl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight sm:text-4xl">Edit {terminology.household.singular}</h1>
        <Link to="/households" className="text-gray-500 hover:text-gray-900 font-medium transition-colors">
          Back to List
        </Link>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="bg-white p-8 rounded-3xl shadow-md space-y-6">

        {apiError && <p className="text-center text-red-600 bg-red-100 p-3 rounded mb-4">{apiError}</p>}
        {successMessage && <p className="text-center text-green-700 bg-green-100 p-3 rounded mb-4 whitespace-pre-line">{successMessage}</p>}

        {/* Supplier Details Section */}
        <div className="border border-gray-300 rounded-lg p-6 bg-gray-50">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Supplier Details</h3>

          {/* Supplier Name */}
          <div className="relative mb-6">
            <label htmlFor="clientNameSearch" className="block text-sm font-medium text-gray-700">Supplier Name * (Assign this {terminology.household.singular} to a {terminology.client.singular})</label>
            <input
              id="clientNameSearch"
              type="text"
              {...register("clientNameSearch", { required: `${terminology.client.singular} Name search is required.` })}
              value={supplierNameSearchValue}
              onChange={handleSupplierSearchChange}
              onBlur={() => setTimeout(() => setShowSupplierSuggestions(false), 150)}
              onFocus={() => supplierSuggestions.length > 0 && setShowSupplierSuggestions(true)}
              autoComplete="off"
              className={`mt-1 block w-full px-3 py-2.5 px-4 border ${errors.clientNameSearch || (errors.clientId && !watch('clientId')) ? 'border-red-500' : 'border-gray-300'} rounded-2xl shadow-sm focus:outline-none focus:ring-green-600 focus:border-green-600 sm:text-sm bg-white`}
            />
            <input type="hidden" {...register("clientId", { required: `Please select a ${terminology.client.singular.toLowerCase()} from the search results.` })} />

            {errors.clientNameSearch && <p className="mt-1 text-xs text-red-600">{errors.clientNameSearch.message}</p>}
            {errors.clientId && !watch('clientId') && <p className="mt-1 text-xs text-red-600">{errors.clientId.message}</p>}

            {showSupplierSuggestions && (
              <ul className="absolute z-10 w-full bg-white rounded-2xl border-0 shadow-sm ring-1 ring-inset ring-gray-300 mt-1 max-h-60 overflow-y-auto shadow-lg">
                {isSearchingSuppliers ? (
                  <li className="px-3 py-2 text-gray-500">Searching...</li>
                ) : supplierSuggestions.length > 0 ? (
                  supplierSuggestions.map((supplier) => (
                    <li
                      key={supplier.id}
                      className="px-3 py-2 hover:bg-green-600-light cursor-pointer"
                      onMouseDown={() => handleSelectSupplier(supplier)}
                    >
                      {supplier.clientName}
                    </li>
                  ))
                ) : (
                   supplierSearchTerm.length >= 3 && <li className="px-3 py-2 text-gray-500">No {terminology.client.plural.toLowerCase()} found.</li>
                )}
              </ul>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Changing the supplier will automatically transfer pending jobs to the new supplier.
            </p>
          </div>

          {/* Case Manager Contact Details */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Case Manager Contact Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="siteContact.name" className="block text-xs font-medium text-gray-600 mb-1">Name:</label>
                <input
                  id="siteContact.name"
                  type="text"
                  {...register("siteContact.name")}
                  className="mt-1 block w-full rounded-2xl border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6 bg-white"
                />
              </div>
              <div>
                <label htmlFor="siteContact.phone" className="block text-xs font-medium text-gray-600 mb-1">Phone:</label>
                <input
                  id="siteContact.phone"
                  type="tel"
                  {...register("siteContact.phone")}
                  className="mt-1 block w-full rounded-2xl border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6 bg-white"
                />
              </div>
              <div>
                <label htmlFor="siteContact.email" className="block text-xs font-medium text-gray-600 mb-1">Email:</label>
                <input
                  id="siteContact.email"
                  type="email"
                  {...register("siteContact.email", {
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address."
                    }
                  })}
                  className={`mt-1 block w-full px-3 py-2.5 px-4 border ${errors.siteContact?.email ? 'border-red-500' : 'border-gray-300'} rounded-2xl shadow-sm focus:outline-none focus:ring-green-600 focus:border-green-600 sm:text-sm bg-white`}
                />
                {errors.siteContact?.email && <p className="mt-1 text-xs text-red-600">{errors.siteContact.email.message}</p>}
              </div>
            </div>
          </div>
        </div>

        {/* Editable Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
                <InputField label="First Name *" id="firstName"
                    register={register("firstName", { required: "First Name is required." })}
                    error={errors.firstName}
                />
            </div>
            <div>
                <InputField label="Last Name" id="lastName"
                    register={register("lastName")}
                />
            </div>
            <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
                <input
                    id="email"
                    type="email"
                    {...register("email", {
                        pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Invalid email address."
                        }
                    })}
                    className={`mt-1 block w-full px-3 py-2 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
                />
                 {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>}
            </div>
            <div>
                <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">Phone Number</label>
                <input
                    id="phoneNumber"
                    type="tel"
                    {...register("phoneNumber")}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
                />
            </div>
        </div>

        <div>
          <label htmlFor="addressLine1" className="block text-sm font-medium text-gray-700">Address Line 1 *</label>
          <div className="relative">
            <input
              id="addressLine1"
              type="text"
              value={addressLine1Value}
              onChange={handleAddressSearchChange}
              onBlur={() => setTimeout(() => setShowAddressSuggestions(false), 150)}
              autoComplete="new-password"
              name="addressLine1-autocomplete-off"
              form="no-autocomplete"
              className={`mt-1 block w-full rounded-2xl border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ${errors.addressLine1 ? "ring-red-500 bg-red-50" : "ring-gray-300"} placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6`}
              placeholder="Start typing an address..."
            />
            {/* Hidden input for react-hook-form validation */}
            <input
              type="hidden"
              {...register("addressLine1", { required: "Address Line 1 is required." })}
              value={addressLine1Value}
            />
            
            {/* Address suggestions dropdown */}
            {showAddressSuggestions && addressSuggestions.length > 0 && (
              <div className="absolute z-10 mt-1 w-full rounded-3xl bg-white shadow-lg">
                <ul className="max-h-60 rounded-md text-sm ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
                  {addressSuggestions.map((address, index) => (
                    <li
                      key={index}
                      className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100"
                      onMouseDown={() => handleSelectAddress(address)}
                    >
                      <span className="block truncate">{address.description}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          {errors.addressLine1 && <p className="mt-1 text-xs text-red-600">{errors.addressLine1.message}</p>}
        </div>

        <div>
            <InputField label="Address Line 2" id="addressLine2"
                register={register("addressLine2")}
            />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6"> {/* Adjusted grid for State, Postcode, Country */}
            <div>
                <InputField label="State" id="state"
                    register={register("state")}
                />
            </div>
            <div>
                <InputField label="Postal Code" id="postalCode"
                    register={register("postalCode")}
                />
            </div>
            {/* Country Field (Read-only) */}
            <div>
                <InputField label="Country" id="country"
                    register={register("country")}
                    readOnly disabled value="Australia"
                />
            </div>
        </div>


        {/* Additional Site Contacts */}
        <div className="border-t pt-6 mt-6 border-gray-200">
            <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                    <h4 className="text-sm font-medium text-gray-700">Additional Site Contacts</h4>
                    <button
                        type="button"
                        onClick={() => appendAdditionalContact({ name: '', phone: '', email: '' })}
                        className="bg-green-600 hover:bg-green-600-dark text-white p-1 rounded transition duration-300 flex items-center justify-center"
                        title="Add Contact"
                    >
                        <FaPlus className="text-xs" />
                    </button>
                </div>
                {additionalContactFields.length > 0 ? (
                    <div className="space-y-3">
                        {additionalContactFields.map((field, index) => (
                            <div key={field.id} className="bg-gray-50 p-4 rounded-2xl">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label htmlFor={`additionalContacts.${index}.name`} className="block text-xs font-medium text-gray-600 mb-1">Name:</label>
                                        <input
                                            id={`additionalContacts.${index}.name`}
                                            type="text"
                                            {...register(`additionalContacts.${index}.name`)}
                                            className="mt-1 block w-full rounded-2xl border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor={`additionalContacts.${index}.phone`} className="block text-xs font-medium text-gray-600 mb-1">Phone:</label>
                                        <input
                                            id={`additionalContacts.${index}.phone`}
                                            type="tel"
                                            {...register(`additionalContacts.${index}.phone`)}
                                            className="mt-1 block w-full rounded-2xl border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                                        />
                                    </div>
                                    <div className="flex gap-2">
                                        <div className="flex-1">
                                            <label htmlFor={`additionalContacts.${index}.email`} className="block text-xs font-medium text-gray-600 mb-1">Email:</label>
                                            <input
                                                id={`additionalContacts.${index}.email`}
                                                type="email"
                                                {...register(`additionalContacts.${index}.email`, {
                                                    pattern: {
                                                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                        message: "Invalid email address."
                                                    }
                                                })}
                                                className={`mt-1 block w-full px-3 py-2.5 px-4 border ${errors.additionalContacts?.[index]?.email ? 'border-red-500' : 'border-gray-300'} rounded-2xl shadow-sm focus:outline-none focus:ring-green-600 focus:border-green-600 sm:text-sm`}
                                            />
                                            {errors.additionalContacts?.[index]?.email && <p className="mt-1 text-xs text-red-600">{errors.additionalContacts[index].email.message}</p>}
                                        </div>
                                        <div className="flex items-end">
                                            <button
                                                type="button"
                                                onClick={() => removeAdditionalContact(index)}
                                                className="bg-red-500 hover:bg-red-600 text-white p-1 rounded transition duration-300 mb-1 flex items-center justify-center"
                                                title="Remove Contact"
                                            >
                                                <FaTrashAlt className="text-xs" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-gray-500 italic">No additional site contacts. Click "Add Contact" to add one.</p>
                )}
            </div>

            {/* Name from Trak */}
            <div className="mb-4">
                <InputField label="Name from Trak" id="originalName"
                    register={register("originalName")}
                    hint="Originally provided name (if different from current name)."
                />
            </div>

            {/* Admin Notes */}
            <div className="mb-4">
                <TextareaField label="Admin Notes" id="adminNotes" rows={4}
                    register={register("adminNotes")}
                    placeholder="Add any internal notes about this household..."
                    hint="Internal notes for administrative use only."
                />
            </div>
            
        </div>

        {/* Status Field (radio buttons) */}
        <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <div className="flex items-center gap-6">
                <label className="inline-flex items-center">
                    <input
                        type="radio"
                        value="active"
                        {...register("status", { required: true })}
                        className="form-radio text-green-600 focus:ring-green-600"
                    />
                    <span className="ml-2">Active</span>
                </label>
                <label className="inline-flex items-center">
                    <input
                        type="radio"
                        value="inactive"
                        {...register("status", { required: true })}
                        className="form-radio text-green-600 focus:ring-green-600"
                    />
                    <span className="ml-2">Inactive</span>
                </label>
            </div>
            {errors.status && <p className="mt-1 text-xs text-red-600">Status is required.</p>}
        </div>

       
        <div className="fixed bottom-0 left-0 right-0 z-10 bg-white border-t border-gray-200 shadow-[0_-2px_6px_rgba(0,0,0,0.08)]">
              <div className="container mx-auto px-6 py-3 max-w-3xl flex justify-end gap-3">
          <Link to="/households" className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded transition duration-300">
            Cancel
          </Link>
          <button
            type="submit"
            disabled={isSubmitting || !isDirty} // Disable if submitting or no changes made
            className="bg-green-600 hover:bg-green-500 text-white font-semibold py-2 px-5 rounded-2xl shadow-sm focus:outline-none focus:shadow-outline transition duration-300 disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : `Save Changes`}
          </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditHouseholdPage;
